/**
 * Financial-year report pack for the CA / accountant: summary, P&L, balance sheet,
 * trial balance, cash & bank summary and the ERP's own checks, as one PDF or Excel file.
 */
import type { ErpSnapshot } from '../api';
import { downloadFile, exportHtmlToPdf, resolveCompanyBrand } from './exportReports';

/** Numbers are amounts (formatted per output); strings are shown as-is. */
type Cell = string | number | null;
type ReportRow = { cells: Cell[]; kind?: 'heading' | 'total' | 'grand' };
export type FyReportSection = { title: string; columns: string[]; rows: ReportRow[]; textColumns?: number[] };

/** Indian grouping, two decimals, negatives in brackets as accountants expect. */
export function caMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '';
  const v = Number(n);
  const s = Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
}

function ymdLabel(ymd: string | null | undefined): string {
  if (!ymd) return '—';
  const d = new Date(`${ymd}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return ymd;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const r2 = (v: number | undefined | null) => Math.round((Number(v) || 0) * 100) / 100;
const blankZero = (v: number | undefined | null): Cell => (r2(v) ? r2(v) : '');
const heading = (label: string, cols: number): ReportRow => ({ cells: [label, ...Array(cols - 1).fill(null)], kind: 'heading' });

export function buildFyReportSections(snap: ErpSnapshot): FyReportSection[] {
  const { profitAndLoss: pl, balanceSheet: bs, trialBalance: tb, cashFlow: cf } = snap;
  const asAt = ymdLabel(snap.period.to);
  const checks = snap.checks || [];
  const bsDifference = bs.difference ?? r2(bs.totalAssets - bs.totalLiabilities - bs.totalEquity);
  const bsOk = bs.balanced ?? Math.abs(bsDifference) < 0.01;
  const memo = snap.receivablesMemo && snap.receivablesMemo.overdueAmount > 0 ? snap.receivablesMemo : null;

  const summary: FyReportSection = {
    title: 'Summary',
    columns: ['Particulars', 'Amount (₹)'],
    rows: [
      { cells: ['Period', `${ymdLabel(snap.period.from)} to ${asAt}`] },
      { cells: ['Financial year', snap.financialYear?.label || '—'] },
      { cells: ['Books start date (opening balances)', ymdLabel(snap.booksStart)] },
      { cells: ['Total income', r2(pl.totalIncome)] },
      { cells: ['Total expenses', r2(pl.totalExpenses)] },
      { cells: [pl.netProfit >= 0 ? 'Net profit' : 'Net loss', r2(pl.netProfit)], kind: 'total' },
      { cells: ['Total assets', r2(bs.totalAssets)] },
      { cells: ['Cash in hand', r2(cf.closingCash)] },
      { cells: ['Bank balance', r2(cf.closingBank)] },
      { cells: ['Loans receivable (principal outstanding)', r2(snap.portfolio.outstanding)] },
      ...(memo
        ? [
            { cells: [`EMIs overdue at ${ymdLabel(memo.asOf)} (memo — not in income)`, r2(memo.overdueAmount)] },
            { cells: ['of which interest (approx.)', r2(memo.overdueInterest)] },
          ]
        : []),
      { cells: ['Trial balance agrees', tb.balanced ? 'Yes' : `No — difference ${caMoney(tb.difference)}`] },
      { cells: ['Balance sheet agrees', bsOk ? 'Yes' : `No — difference ${caMoney(bsDifference)}`] },
    ],
  };

  const plRows: ReportRow[] = [heading('Income', 2)];
  pl.income.forEach((r) => plRows.push({ cells: [r.name, r2(r.amount)] }));
  plRows.push({ cells: ['Total income', r2(pl.totalIncome)], kind: 'total' });
  plRows.push(heading('Expenses', 2));
  pl.expenses.forEach((r) => plRows.push({ cells: [r.name, r2(r.amount)] }));
  plRows.push({ cells: ['Total expenses', r2(pl.totalExpenses)], kind: 'total' });
  plRows.push({
    cells: [pl.netProfit >= 0 ? 'Net profit for the period' : 'Net loss for the period', r2(pl.netProfit)],
    kind: 'grand',
  });

  const bsRows: ReportRow[] = [heading('Capital & liabilities', 2)];
  bs.equity.forEach((r) => bsRows.push({ cells: [r.name, r2(r.amount)] }));
  bsRows.push({ cells: ['Total capital', r2(bs.totalEquity)], kind: 'total' });
  bs.liabilities.forEach((r) => bsRows.push({ cells: [r.name, r2(r.amount)] }));
  if (bs.liabilities.length) bsRows.push({ cells: ['Total liabilities', r2(bs.totalLiabilities)], kind: 'total' });
  bsRows.push({
    cells: ['Total capital & liabilities', r2(bs.totalLiabilitiesAndEquity ?? bs.totalLiabilities + bs.totalEquity)],
    kind: 'grand',
  });
  bsRows.push(heading('Assets', 2));
  bs.assets.forEach((r) => bsRows.push({ cells: [r.name, r2(r.amount)] }));
  bsRows.push({ cells: ['Total assets', r2(bs.totalAssets)], kind: 'grand' });

  const drCr = (n: number | undefined): Cell => {
    const v = r2(n);
    if (!v) return '';
    return `${caMoney(Math.abs(v))} ${v > 0 ? 'Dr' : 'Cr'}`;
  };
  const tbRows: ReportRow[] = tb.rows.map((r) => ({
    cells: [
      r.name,
      drCr(r.opening),
      blankZero(r.periodDebit),
      blankZero(r.periodCredit),
      r.side === 'Dr' ? blankZero(r.balance) : '',
      r.side === 'Cr' ? blankZero(r.balance) : '',
    ],
  }));
  const sumClosing = (side: 'Dr' | 'Cr') => tb.rows.reduce((s, r) => s + (r.side === side ? r.balance : 0), 0);
  tbRows.push({
    cells: [
      'Total',
      '',
      r2(tb.rows.reduce((s, r) => s + (r.periodDebit || 0), 0)),
      r2(tb.rows.reduce((s, r) => s + (r.periodCredit || 0), 0)),
      r2(tb.closingDebit ?? sumClosing('Dr')),
      r2(tb.closingCredit ?? sumClosing('Cr')),
    ],
    kind: 'grand',
  });

  const n = (v: number | undefined) => Number(v) || 0;
  const flowLine = (label: string, cash: number, bank: number, kind?: ReportRow['kind']): ReportRow => ({
    cells: [label, r2(cash), r2(bank), r2(cash + bank)],
    kind,
  });
  const cashRows: ReportRow[] = [
    flowLine('Opening balance', n(cf.openingCash), n(cf.openingBank), 'total'),
    flowLine('Add: EMI / loan collections (incl. interest & late fees)', n(cf.cashCollections), n(cf.bankCollections)),
    flowLine('Add: Vehicle sales', n(cf.cashSales), n(cf.bankSales)),
    flowLine('Less: Loans given', -n(cf.cashDisbursements), -n(cf.bankDisbursements)),
    flowLine('Less: Expenses, salaries & advances', -n(cf.cashExpenses), -n(cf.bankExpenses)),
    flowLine('Less: Vehicle purchases', -n(cf.cashPurchases), -n(cf.bankPurchases)),
  ];
  if (n(cf.cashOther) || n(cf.bankOther)) cashRows.push(flowLine('Other journal entries (net)', n(cf.cashOther), n(cf.bankOther)));
  cashRows.push(flowLine('Closing balance', n(cf.closingCash), n(cf.closingBank), 'grand'));

  const statusLabel: Record<string, string> = { ok: 'OK', warning: 'Review', error: 'Error', info: 'Note' };
  const noteRows: ReportRow[] = checks.map((c) => ({ cells: [statusLabel[c.status] || c.status, c.message] }));
  if (pl.basis) noteRows.push({ cells: ['Basis', pl.basis] });
  if (memo) {
    noteRows.push({
      cells: [
        'Note',
        `${caMoney(memo.overdueAmount)} of EMIs from ${memo.overdueLoans} loan${memo.overdueLoans === 1 ? '' : 's'} fell due by ${ymdLabel(memo.asOf)} but was not received (about ${caMoney(memo.overdueInterest)} of it interest). Interest is booked when received, so this is not in income; the principal part is already within Loans receivable.`,
      ],
    });
  }
  noteRows.push({
    cells: ['Basis', 'Loans migrated from earlier books are part of the opening “Loans receivable”; only their later receipts are recorded here.'],
  });
  noteRows.push({ cells: ['Basis', snap.disclaimer] });

  return [
    { ...summary, textColumns: [0] },
    { title: `Profit & Loss Account — ${ymdLabel(snap.period.from)} to ${asAt}`, columns: ['Particulars', 'Amount (₹)'], rows: plRows },
    { title: `Balance Sheet as at ${asAt}`, columns: ['Particulars', 'Amount (₹)'], rows: bsRows },
    {
      title: `Trial Balance as at ${asAt}`,
      columns: ['Account', 'Opening', 'Debit (period, ₹)', 'Credit (period, ₹)', 'Closing Dr (₹)', 'Closing Cr (₹)'],
      rows: tbRows,
    },
    { title: 'Cash & Bank Summary', columns: ['Particulars', 'Cash (₹)', 'Bank (₹)', 'Total (₹)'], rows: cashRows },
    { title: 'Checks & Notes', columns: ['Status', 'Detail'], rows: noteRows, textColumns: [0, 1] },
  ];
}

function esc(s: Cell): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sectionTableHtml(section: FyReportSection, forExcel: boolean): string {
  const textCols = new Set(section.textColumns ?? [0]);
  const rowStyle = (kind?: ReportRow['kind']) => {
    if (kind === 'heading') return 'font-weight:700;background:#eef2ff';
    if (kind === 'total') return 'font-weight:700';
    if (kind === 'grand') return 'font-weight:800;border-top:2px solid #334155;background:#f1f5f9';
    return '';
  };
  const cell = (c: Cell, i: number, kind?: ReportRow['kind']) => {
    const styles = [rowStyle(kind)];
    const isAmount = typeof c === 'number';
    if (!textCols.has(i)) styles.push('text-align:right');
    if (isAmount && forExcel) styles.push("mso-number-format:'#,##0.00;(#,##0.00)'");
    const text = isAmount ? (forExcel ? String(c) : caMoney(c)) : esc(c);
    const style = styles.filter(Boolean).join(';');
    return `<td${style ? ` style="${style}"` : ''}>${text}</td>`;
  };
  const head = `<tr>${section.columns
    .map((c, i) => `<th style="white-space:nowrap${textCols.has(i) ? '' : ';text-align:right'}">${esc(c)}</th>`)
    .join('')}</tr>`;
  const body = section.rows.map((r) => `<tr>${r.cells.map((c, i) => cell(c, i, r.kind)).join('')}</tr>`).join('');
  return `<table class="mf-portrait-report"${forExcel ? ' border="1"' : ''}><thead>${head}</thead><tbody>${body}</tbody></table>`;
}

export function fyReportTitle(snap: ErpSnapshot): string {
  const label = snap.financialYear?.label || `${snap.period.from || ''} to ${snap.period.to || ''}`;
  return `Financial Year Report ${label}`.trim();
}

export function exportFyReportPdf(snap: ErpSnapshot): Promise<void> {
  const html = buildFyReportSections(snap)
    .map(
      (s) =>
        `<h3 style="font-size:14px;font-weight:800;margin:18px 0 6px;color:#0f172a;page-break-after:avoid">${esc(s.title)}</h3>${sectionTableHtml(s, false)}`
    )
    .join('');
  return exportHtmlToPdf(fyReportTitle(snap).replace(/(\d)-(\d)/g, '$1–$2'), html, { landscape: false });
}

export function exportFyReportExcel(snap: ErpSnapshot): void {
  const title = fyReportTitle(snap);
  const brand = resolveCompanyBrand();
  const sections = buildFyReportSections(snap)
    .map((s) => `<h3>${esc(s.title)}</h3>${sectionTableHtml(s, true)}<br/>`)
    .join('');
  const workbook = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8" /><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>FY Report</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
      <body>
        <h2>${esc(brand.name)}</h2>
        ${brand.address ? `<p>${esc(brand.address)}</p>` : ''}
        ${brand.gstin ? `<p>GSTIN: ${esc(brand.gstin)}</p>` : ''}
        <h2>${esc(title)}</h2>
        <p>Generated on ${esc(new Date().toLocaleString('en-IN'))}</p>
        ${sections}
      </body>
    </html>`;
  const blob = new Blob(['\uFEFF', workbook], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  void downloadFile(blob, `${title.replace(/[^\w-]+/g, '-').replace(/-+/g, '-').toLowerCase()}.xls`);
}
